import { Control, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Buttons/Button';
import { ContactKind } from './accessErrors';

type InvitationFields = {
    email?: string;
    phoneNumber?: string;
    invitationMethod?: ContactKind;
};

/** Picks where a new invitation is sent. Shown only when both contacts are entered. */
export const InvitationMethodField = <T extends InvitationFields>({
    control,
}: {
    control: Control<T>;
}) => {
    const { t } = useTranslation();
    const typedControl = control as unknown as Control<InvitationFields>;
    const [email, phoneNumber] = useWatch({
        control: typedControl,
        name: ['email', 'phoneNumber'],
    });
    if (!email?.trim() || !phoneNumber) return null;

    return (
        <Controller
            control={typedControl}
            name="invitationMethod"
            render={({ field: { value = 'email', onChange } }) => (
                <div className="col-span-full flex flex-col gap-2">
                    <span className="text-sm">
                        {t('accountAccess.invitation.sendBy')}
                    </span>
                    <div
                        role="radiogroup"
                        aria-label={t('accountAccess.invitation.sendBy')}
                        className="flex gap-2"
                    >
                        {(['email', 'phone'] as const).map(kind => (
                            <Button
                                key={kind}
                                active={value === kind}
                                title={t(
                                    kind === 'phone'
                                        ? 'accountAccess.invitation.bySms'
                                        : 'accountAccess.invitation.byEmail',
                                )}
                                onClick={() => onChange(kind)}
                            />
                        ))}
                    </div>
                </div>
            )}
        />
    );
};

/** The server needs a method only when both contacts exist. */
export const invitationMethodFor = (
    email: string | undefined,
    phoneNumber: string | undefined,
    chosen: ContactKind | undefined,
): ContactKind =>
    email && phoneNumber ? chosen ?? 'email' : phoneNumber ? 'phone' : 'email';
